import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2 } from "lucide-react";

const BLANK = {
  name: "", slug: "", tagline: "", logo_url: "", min_deposit: "$100", status: "active",
  regulatory_jurisdictions: "", platforms: "", markets: "", account_types: "", features: "",
  regulatory_information: "", overview: "", company_information: "", spreads_info: "",
  deposit_withdrawal_info: "", external_url: "", tracking_url: "", seo_title: "", seo_description: "",
  name_ar: "", tagline_ar: "", overview_ar: "", company_information_ar: "", regulatory_information_ar: "",
  spreads_info_ar: "", deposit_withdrawal_info_ar: "", regulatory_jurisdictions_ar: "", platforms_ar: "",
  markets_ar: "", account_types_ar: "", features_ar: "", advantages_ar: "", considerations_ar: "",
};

const AR_ARR_FIELDS = ["regulatory_jurisdictions_ar", "platforms_ar", "markets_ar", "account_types_ar", "features_ar", "advantages_ar", "considerations_ar"];

const arr = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const joinArr = (a) => (Array.isArray(a) ? a.join(", ") : a || "");

export default function BrokersAdmin() {
  const qc = useQueryClient();
  const { data: brokers = [] } = useQuery({ queryKey: ["admin-brokers"], queryFn: async () => (await api.get("/admin/brokers")).data });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        regulatory_jurisdictions: arr(form.regulatory_jurisdictions),
        platforms: arr(form.platforms), markets: arr(form.markets),
        account_types: arr(form.account_types), features: arr(form.features),
      };
      AR_ARR_FIELDS.forEach((k) => { payload[k] = arr(form[k]); });
      if (editId) return api.put(`/admin/brokers/${editId}`, payload);
      return api.post("/admin/brokers", payload);
    },
    onSuccess: () => { toast.success("Saved"); qc.invalidateQueries({ queryKey: ["admin-brokers"] }); setOpen(false); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => api.delete(`/admin/brokers/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["admin-brokers"] }); },
  });

  const openNew = () => { setForm(BLANK); setEditId(null); setOpen(true); };
  const openEdit = (b) => {
    setForm({
      ...BLANK, ...b,
      regulatory_jurisdictions: joinArr(b.regulatory_jurisdictions), platforms: joinArr(b.platforms),
      markets: joinArr(b.markets), account_types: joinArr(b.account_types), features: joinArr(b.features),
      regulatory_jurisdictions_ar: joinArr(b.regulatory_jurisdictions_ar), platforms_ar: joinArr(b.platforms_ar),
      markets_ar: joinArr(b.markets_ar), account_types_ar: joinArr(b.account_types_ar), features_ar: joinArr(b.features_ar),
      advantages_ar: joinArr(b.advantages_ar), considerations_ar: joinArr(b.considerations_ar),
    });
    setEditId(b.id); setOpen(true);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-bold text-ink">Broker Partners</h1><p className="mt-1 text-sm text-subtle">Manage brokerage partner listings.</p></div>
        <Button onClick={openNew} className="bg-finblue hover:bg-finblueDark" data-testid="broker-add"><Plus className="h-4 w-4" /> Add Broker</Button>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-hair bg-white shadow-sm">
        <Table>
          <TableHeader><TableRow className="bg-canvas"><TableHead>Name</TableHead><TableHead>Markets</TableHead><TableHead>Min. Deposit</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {brokers.map((b) => (
              <TableRow key={b.id} data-testid={`broker-row-${b.slug}`}>
                <TableCell className="font-medium text-ink">{b.name}</TableCell>
                <TableCell className="text-sm text-subtle">{joinArr(b.markets)}</TableCell>
                <TableCell className="text-sm">{b.min_deposit}</TableCell>
                <TableCell><span className={`rounded px-2 py-0.5 text-xs ${b.status === "active" ? "bg-success/10 text-success" : "bg-subtle/10 text-subtle"}`}>{b.status}</span></TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(b)} data-testid={`broker-edit-${b.slug}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del.mutate(b.id)} data-testid={`broker-delete-${b.slug}`}><Trash2 className="h-4 w-4 text-danger" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Broker" : "Add Broker"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <F label="Name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="broker-form-name" /></F>
            <F label="Slug (optional)"><Input value={form.slug} onChange={(e) => set("slug", e.target.value)} /></F>
            <F label="Tagline" full><Input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} /></F>
            <F label="Logo (upload or URL)" full>
              <div className="flex items-center gap-3">
                {form.logo_url && <img src={form.logo_url} alt="logo" className="h-10 w-10 rounded border border-hair object-contain" />}
                <input
                  type="file"
                  accept="image/*"
                  data-testid="broker-form-logo-file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 1024 * 1024) { alert("Please use an image under 1MB."); return; }
                    const reader = new FileReader();
                    reader.onload = () => set("logo_url", reader.result);
                    reader.readAsDataURL(file);
                  }}
                  className="text-sm"
                />
                {form.logo_url && <button type="button" onClick={() => set("logo_url", "")} className="text-xs text-danger">Remove</button>}
              </div>
              <Input className="mt-2" placeholder="or paste image URL" value={form.logo_url?.startsWith("data:") ? "" : form.logo_url} onChange={(e) => set("logo_url", e.target.value)} />
            </F>
            <F label="Min. Deposit"><Input value={form.min_deposit} onChange={(e) => set("min_deposit", e.target.value)} /></F>
            <F label="Jurisdictions (comma-separated)"><Input value={form.regulatory_jurisdictions} onChange={(e) => set("regulatory_jurisdictions", e.target.value)} /></F>
            <F label="Platforms (comma-separated)"><Input value={form.platforms} onChange={(e) => set("platforms", e.target.value)} /></F>
            <F label="Markets (comma-separated)"><Input value={form.markets} onChange={(e) => set("markets", e.target.value)} /></F>
            <F label="Account Types (comma-separated)"><Input value={form.account_types} onChange={(e) => set("account_types", e.target.value)} /></F>
            <F label="Features (comma-separated)" full><Input value={form.features} onChange={(e) => set("features", e.target.value)} /></F>
            <F label="Overview" full><Textarea rows={3} value={form.overview} onChange={(e) => set("overview", e.target.value)} /></F>
            <F label="Regulatory Information" full><Textarea rows={2} value={form.regulatory_information} onChange={(e) => set("regulatory_information", e.target.value)} /></F>
            <F label="Spreads / Commissions"><Input value={form.spreads_info} onChange={(e) => set("spreads_info", e.target.value)} /></F>
            <F label="Deposit/Withdrawal Info"><Input value={form.deposit_withdrawal_info} onChange={(e) => set("deposit_withdrawal_info", e.target.value)} /></F>
            <F label="External URL"><Input value={form.external_url} onChange={(e) => set("external_url", e.target.value)} /></F>
            <F label="Tracking URL"><Input value={form.tracking_url} onChange={(e) => set("tracking_url", e.target.value)} /></F>
            <F label="Status"><Input value={form.status} onChange={(e) => set("status", e.target.value)} placeholder="active / archived" /></F>

            <div className="sm:col-span-2 mt-2 rounded-lg border border-hair bg-canvas p-4">
              <p className="text-sm font-semibold text-ink" dir="rtl">المحتوى بالعربية · Arabic Content (optional — falls back to English)</p>
              <p className="mt-1 text-xs text-subtle">Fill these to show the profile fully in Arabic. Comma-separated fields accept Arabic values.</p>
            </div>
            <F label="Name (Arabic)"><Input dir="rtl" value={form.name_ar} onChange={(e) => set("name_ar", e.target.value)} data-testid="broker-form-name-ar" /></F>
            <F label="Tagline (Arabic)"><Input dir="rtl" value={form.tagline_ar} onChange={(e) => set("tagline_ar", e.target.value)} data-testid="broker-form-tagline-ar" /></F>
            <F label="Jurisdictions AR (comma-separated)"><Input dir="rtl" value={form.regulatory_jurisdictions_ar} onChange={(e) => set("regulatory_jurisdictions_ar", e.target.value)} data-testid="broker-form-regulatory-jurisdictions-ar" /></F>
            <F label="Platforms AR (comma-separated)"><Input dir="rtl" value={form.platforms_ar} onChange={(e) => set("platforms_ar", e.target.value)} data-testid="broker-form-platforms-ar" /></F>
            <F label="Markets AR (comma-separated)"><Input dir="rtl" value={form.markets_ar} onChange={(e) => set("markets_ar", e.target.value)} data-testid="broker-form-markets-ar" /></F>
            <F label="Account Types AR (comma-separated)"><Input dir="rtl" value={form.account_types_ar} onChange={(e) => set("account_types_ar", e.target.value)} data-testid="broker-form-account-types-ar" /></F>
            <F label="Features AR (comma-separated)" full><Input dir="rtl" value={form.features_ar} onChange={(e) => set("features_ar", e.target.value)} data-testid="broker-form-features-ar" /></F>
            <F label="Advantages AR (comma-separated)" full><Input dir="rtl" value={form.advantages_ar} onChange={(e) => set("advantages_ar", e.target.value)} data-testid="broker-form-advantages-ar" /></F>
            <F label="Considerations AR (comma-separated)" full><Input dir="rtl" value={form.considerations_ar} onChange={(e) => set("considerations_ar", e.target.value)} data-testid="broker-form-considerations-ar" /></F>
            <F label="Overview (Arabic)" full><Textarea dir="rtl" rows={3} value={form.overview_ar} onChange={(e) => set("overview_ar", e.target.value)} data-testid="broker-form-overview-ar" /></F>
            <F label="Company Information (Arabic)" full><Textarea dir="rtl" rows={2} value={form.company_information_ar} onChange={(e) => set("company_information_ar", e.target.value)} data-testid="broker-form-company-information-ar" /></F>
            <F label="Regulatory Information (Arabic)" full><Textarea dir="rtl" rows={2} value={form.regulatory_information_ar} onChange={(e) => set("regulatory_information_ar", e.target.value)} data-testid="broker-form-regulatory-information-ar" /></F>
            <F label="Spreads / Commissions (Arabic)"><Input dir="rtl" value={form.spreads_info_ar} onChange={(e) => set("spreads_info_ar", e.target.value)} data-testid="broker-form-spreads-info-ar" /></F>
            <F label="Deposit/Withdrawal Info (Arabic)"><Input dir="rtl" value={form.deposit_withdrawal_info_ar} onChange={(e) => set("deposit_withdrawal_info_ar", e.target.value)} data-testid="broker-form-deposit-withdrawal-info-ar" /></F>
          </div>
          <DialogFooter><Button onClick={() => save.mutate()} disabled={save.isPending} className="bg-finblue hover:bg-finblueDark" data-testid="broker-form-save">{save.isPending ? "Saving..." : "Save Broker"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, full, children }) {
  return <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}><Label className="text-sm">{label}</Label>{children}</div>;
}
